// components/ProgressLogger.tsx
import { useEffect, useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, Loader2, XCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

interface ProgressLog {
  timestamp: string;
  elapsed: number;
  step: string;
  message: string;
  progress?: number;
  details?: Record<string, any>;
  error?: string;
}

interface ProgressLoggerProps {
  sessionId: string;
  reportId: number;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

export function ProgressLogger({ sessionId, reportId, onComplete, onError }: ProgressLoggerProps) {
  const [logs, setLogs] = useState<ProgressLog[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Ajouter un log initial pour confirmer que le composant est monté
  useEffect(() => {
    if (!sessionId) {
      console.warn("[ProgressLogger] No sessionId provided");
      setConnectionError("Session ID manquant");
      return;
    }

    console.log(`[ProgressLogger] Component mounted with sessionId=${sessionId.substring(0, 8)}...`);
    
    // Ajouter un log initial visible immédiatement
    setLogs([{
      timestamp: new Date().toISOString(),
      elapsed: 0,
      step: "info",
      message: "Initialisation de la connexion...",
      progress: 0,
      details: { sessionId: sessionId.substring(0, 8) + "..." }
    }]);
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;

    console.log(`[ProgressLogger] Starting connection for sessionId=${sessionId.substring(0, 8)}...`);

    // Récupérer le token (peut être "token" ou "auth_token")
    let token = localStorage.getItem("token") || localStorage.getItem("auth_token");
    if (token && token.toLowerCase().startsWith("bearer ")) {
      token = token.slice(7).trim();
    }
    if (!token) {
      console.error("[ProgressLogger] No token found");
      const errorMsg = "Token d'authentification manquant";
      setConnectionError(errorMsg);
      setLogs(prev => [...prev, {
        timestamp: new Date().toISOString(),
        elapsed: 0,
        step: "error",
        message: "Erreur: Token d'authentification manquant",
        progress: 0,
        error: errorMsg,
        details: {}
      }]);
      if (onError) onError(errorMsg);
      return;
    }

    // Utiliser fetch avec ReadableStream pour SSE (car EventSource ne supporte pas les headers)
    const url = `${API_BASE}/reports/${reportId}/progress/${sessionId}`;
    console.log(`[ProgressLogger] Connecting to ${url}`);
    const controller = new AbortController();
    
    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "text/event-stream",
      },
      signal: controller.signal,
    })
      .then((response) => {
        console.log(`[ProgressLogger] Response status: ${response.status}`);
        if (!response.ok) {
          console.error(`[ProgressLogger] Response not OK: ${response.status} ${response.statusText}`);
          throw new Error(`Failed to connect: ${response.status} ${response.statusText}`);
        }
        setIsConnected(true);
        console.log("[ProgressLogger] Connected, starting to read stream...");
        
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        
        if (!reader) return;
        
        let buffer = "";
        
        const readChunk = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split("\n\n");
              buffer = lines.pop() || "";
              
              for (const line of lines) {
                if (line.startsWith("data: ")) {
                  try {
                    const data = JSON.parse(line.slice(6));
                    console.log(`[ProgressLogger] Received log:`, data);
                    setLogs((prev) => {
                      const newLogs = [...prev, data];
                      console.log(`[ProgressLogger] Total logs: ${newLogs.length}`);
                      return newLogs;
                    });
                    
                    // Scroll to bottom
                    setTimeout(() => {
                      scrollRef.current?.scrollTo({
                        top: scrollRef.current.scrollHeight,
                        behavior: "smooth",
                      });
                    }, 100);
                    
                    // Check if complete
                    if (data.step === "success" || data.step === "error") {
                      console.log(`[ProgressLogger] Process completed with step: ${data.step}`);
                      setIsConnected(false);
                      if (data.step === "success" && onComplete) {
                        onComplete();
                      } else if (data.step === "error" && onError) {
                        onError(data.error || data.message);
                      }
                      return;
                    }
                  } catch (e) {
                    console.error("[ProgressLogger] Error parsing SSE data:", e, "Line:", line);
                  }
                }
              }
            }
          } catch (e) {
            if (e instanceof Error && e.name !== "AbortError") {
              console.error("Error reading stream:", e);
              setIsConnected(false);
              if (onError) onError("Connection lost");
            }
          }
        };
        
        readChunk();
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("[ProgressLogger] Error connecting to progress stream:", error);
          setIsConnected(false);
          const errorMsg = error.message || "Échec de connexion au stream de progression";
          setConnectionError(errorMsg);
          setLogs(prev => [...prev, {
            timestamp: new Date().toISOString(),
            elapsed: 0,
            step: "error",
            message: `Erreur de connexion: ${errorMsg}`,
            progress: 0,
            error: errorMsg,
            details: { url }
          }]);
          if (onError) onError(errorMsg);
        }
      });

    return () => {
      controller.abort();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [sessionId, reportId, onComplete, onError]);

  const currentProgress = logs.length > 0 ? logs[logs.length - 1]?.progress ?? 0 : 0;
  const currentStep = logs.length > 0 ? logs[logs.length - 1]?.step : "";
  const isComplete = currentStep === "success";
  const hasError = currentStep === "error";

  const getStepIcon = (step: string) => {
    switch (step) {
      case "success":
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "error":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "execute":
      case "init":
      case "write":
      case "finalize":
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStepColor = (step: string) => {
    switch (step) {
      case "success":
        return "text-green-600";
      case "error":
        return "text-red-600";
      case "execute":
      case "init":
      case "write":
      case "finalize":
        return "text-blue-600";
      default:
        return "text-gray-600";
    }
  };

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Progression</h3>
        <div className="flex items-center gap-2">
          {isConnected && !isComplete && !hasError && (
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
          )}
          <span className="text-xs text-muted-foreground">
            {isConnected ? "En cours..." : isComplete ? "Terminé" : hasError ? "Erreur" : "En attente"}
          </span>
        </div>
      </div>

      {currentProgress > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{Math.round(currentProgress)}%</span>
            {logs.length > 0 && (
              <span>{logs[logs.length - 1]?.elapsed?.toFixed(1)}s</span>
            )}
          </div>
          <Progress value={currentProgress} className="h-2" />
        </div>
      )}

      <div
        ref={scrollRef}
        className="max-h-64 overflow-y-auto space-y-2 text-sm"
        style={{ scrollbarWidth: "thin" }}
      >
        {logs.length === 0 ? (
          <div className="text-center text-muted-foreground py-4">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            <p>En attente des logs...</p>
            {connectionError && (
              <p className="text-red-600 text-xs mt-2">{connectionError}</p>
            )}
          </div>
        ) : (
          logs.map((log, index) => (
            <div
              key={index}
              className={cn(
                "flex items-start gap-2 p-2 rounded border",
                log.step === "error" && "bg-red-50 border-red-200",
                log.step === "success" && "bg-green-50 border-green-200"
              )}
            >
              {getStepIcon(log.step)}
              <div className="flex-1 min-w-0">
                <div className={cn("font-medium", getStepColor(log.step))}>
                  {log.message}
                </div>
                {log.details && Object.keys(log.details).length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {Object.entries(log.details).map(([key, value]) => (
                      <span key={key} className="mr-2">
                        {key}: {String(value)}
                      </span>
                    ))}
                  </div>
                )}
                {log.error && (
                  <div className="text-xs text-red-600 mt-1">{log.error}</div>
                )}
                <div className="text-xs text-muted-foreground mt-1">
                  {new Date(log.timestamp).toLocaleTimeString()} ({log.elapsed}s)
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
}

